import { AppLogoHeader, AppLogoHeaderLight } from 'components/_common/Icons';
import {
  CustomerFacebook,
  CustomerInstagram,
  CustomerTwitter,
  CustomerYoutube,
} from 'components/_common/Icons';
import ContactIconSvg from 'components/_common/Icons/SVGIcons/AboutIconSvg';
import BlogIconSvg from 'components/_common/Icons/SVGIcons/BlogIconSvg';
import CompareSvg from 'components/_common/Icons/SVGIcons/CompareSvg';
import FilterAdjustmentSvg from 'components/_common/Icons/SVGIcons/FilterAdjustmentSvg';
import HamburgerFullSvg from 'components/_common/Icons/SVGIcons/HambergurFullSvg';
import MagnifierSvg from 'components/_common/Icons/SVGIcons/MagnifierSvg';
import MapIconSvg from 'components/_common/Icons/SVGIcons/MapIconSvg';
import MoonSvg from 'components/_common/Icons/SVGIcons/MoonSvg';
import PlusMenuSvg from 'components/_common/Icons/SVGIcons/PlusMenuSvg';
// import SettingsSvg from 'components/_common/Icons/SVGIcons/SettingsSvg';
import RefreshSvg from 'components/_common/Icons/SVGIcons/RefreshSvg';
import SunSvg from 'components/_common/Icons/SVGIcons/SunSvg';
import UsersIconSvg from 'components/_common/Icons/SVGIcons/UsersIconSvg';
import VerifiedBadgeSvg from 'components/_common/Icons/SVGIcons/VerifiedBadgeSvg';
import { getTruncatedString } from 'helpers/CommonHelper';
import { placeParamsToURL } from 'helpers/CommonHelper';
import { SHIPPING_FFL_NAME_MAX_LENGTH } from 'helpers/DataHelper';
import useAutoSearch from 'hooks/useAutoSearch';
import useDashboardLink from 'hooks/useDashboardLink';
import { t } from 'i18next';
import { Preloader } from 'modules/admin/components/_common/Preloader';
import React, { useState, lazy, Suspense, useEffect, useRef } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { loginRoutes } from 'routes/admin/AdminRoutes';
import { customerMainRoutes, customerProfileRoutes } from 'routes/customer/CustomerRoutes';
import { userProfileInfoSelector } from 'store/auth/selectors/authSelectors';
import {
  appThemeSelector,
  megaMenuCategoriesSelector,
  selectedDeliveryFFLIsRegisteredSelector,
  mobileMenuOpenSelector,
  selectedDeliveryFFLNameSelector,
} from 'store/customer/selectors/CustomerGlobalSelectors';
import {
  fetchHeaderFooterData,
  setAppTheme,
  setDeliveryLocationModalOpen,
  setGuestDeliveryFFLModalOpen,
  setMobileMenuOpen,
} from 'store/customer/slices/CustomerGlobalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { PRODUCT_FILTER_QUERY_KEYS } from 'utils/productQuery';

const SellYourFirearmsModal = lazy(() => import('../Modals/SellYourFirearmsModal'));
import DeliveryLocationBlock from '../Sections/DeliveryLocationBlock';
import MinCartSection from '../Sections/MinCartSection';

const MobileHeaderSection: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  // shared with the bottom bar's Menu button, which opens this same drawer
  const showOffcanvas = useAppSelector(mobileMenuOpenSelector);

  const [showSellModal, setShowSellModal] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  // const isHomePage = location.pathname === '/';
  const isProductPage = location.pathname.includes('/products');
  const isFFLPage = location.pathname.includes('/ffl-listing');
  // const showFilterSort = isProductPage || isFFLPage;

  const userProfileInfo = useAppSelector(userProfileInfoSelector);
  const dashboardLink = useDashboardLink();
  const categoryData = useAppSelector(megaMenuCategoriesSelector);
  // follows the address picked in the header "Deliver to" block
  const selectedFFLName = useAppSelector(selectedDeliveryFFLNameSelector);
  const isSelectedFFLRegistered = useAppSelector(selectedDeliveryFFLIsRegisteredSelector);

  const dispatch = useAppDispatch();

  // Same Redux theme the web header and its toggle use, so the logo and every other
  // theme-aware piece stay in step whichever toggle changed it.
  const theme = useAppSelector(appThemeSelector);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.add('light-theme');
      root.classList.remove('dark-theme');
    } else {
      root.classList.add('dark-theme');
      root.classList.remove('light-theme');
    }
    localStorage.setItem('app-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    dispatch(setAppTheme(theme === 'dark' ? 'light' : 'dark'));
  };
  const [activeCategoryDropdown, setActiveCategoryDropdown] = useState<any | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ left: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false });

  // show the edge gradients only on the side(s) where the category list can still scroll
  const updateScrollEdges = () => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setScrollEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  };

  useEffect(() => {
    updateScrollEdges();
    window.addEventListener('resize', updateScrollEdges);
    return () => window.removeEventListener('resize', updateScrollEdges);
  }, [categoryData]);

  useEffect(() => {
    dispatch(fetchHeaderFooterData());
  }, [dispatch]);

  const [prevPathname, setPrevPathname] = useState(location.pathname);
  const [prevSearch, setPrevSearch] = useState(location.search);

  if (prevPathname !== location.pathname || prevSearch !== location.search) {
    setPrevPathname(location.pathname);
    setPrevSearch(location.search);
    setActiveCategoryDropdown(null);
    setDropdownPosition(null);
  }

  const handleScrollBy = (left: number) => {
    scrollRef.current?.scrollBy({ left, behavior: 'smooth' });
  };

  const handleCategoryClick = (e: React.MouseEvent<HTMLButtonElement>, category: any) => {
    e.stopPropagation();
    if (activeCategoryDropdown?.id === category.id) {
      setActiveCategoryDropdown(null);
      setDropdownPosition(null);
    } else {
      setActiveCategoryDropdown(category);
      const buttonEl = e.currentTarget;
      const containerEl = buttonEl.closest('.mobile-category-menu');
      if (buttonEl && containerEl) {
        const buttonRect = buttonEl.getBoundingClientRect();
        const containerRect = containerEl.getBoundingClientRect();
        let leftOffset = buttonRect.left - containerRect.left;
        const dropdownWidth = 200; // Estimated width
        if (leftOffset + dropdownWidth > containerRect.width) {
          leftOffset = containerRect.width - dropdownWidth - 10;
        }
        if (leftOffset < 10) {
          leftOffset = 10;
        }
        setDropdownPosition({ left: leftOffset });
      }
    }
  };

  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const toggleCategoryExpand = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedCategories((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleClose = () => dispatch(setMobileMenuOpen(false));
  const handleShow = () => dispatch(setMobileMenuOpen(true));

  // opens the same "Select Delivery Location" popup as the header "Deliver to" block
  // (the FFL map picker for guests, who have no address book)
  const handleOpenDeliveryLocation = () => {
    handleClose();
    dispatch(
      userProfileInfo?.id ? setDeliveryLocationModalOpen(true) : setGuestDeliveryFFLModalOpen(true)
    );
  };

  // The modal itself is now role/login-aware (guest, customer, or FFL variants) - always
  // open it rather than skipping straight to a page for logged-in users.
  const handleSellClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleClose();
    setShowSellModal(true);
  };

  const placeholderText = isFFLPage
    ? t('search_ffl_placeholder', 'Search by FFL Dealer Name')
    : t('search_placeholder');

  // Sync search from URL - only when the URL itself changes, not on every
  // keystroke. searchValue was in this effect's own dependency array, so typing
  // (setSearchValue) re-ran the effect immediately, saw the URL hadn't caught up
  // yet, and reset the input back to whatever the URL said (empty, usually) -
  // every character appeared to get wiped the instant it was typed.
  useEffect(() => {
    const setSearchDetails = () => {
      const urlSearch = new URLSearchParams(location.search).get('search') || '';
      setSearchValue(urlSearch);
    };
    setSearchDetails();
  }, [location.search]);

  const handleSearchSubmit = () => {
    if (!searchValue.trim()) return;
    navigate(
      `${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.SEARCH}=${encodeURIComponent(searchValue.trim())}`
    );
  };

  // Search as the user types. On the products page the URL is replaced rather than
  // pushed, so each pause in typing doesn't leave a history entry behind.
  const urlSearch =
    new URLSearchParams(location.search).get(PRODUCT_FILTER_QUERY_KEYS.SEARCH) || '';
  useAutoSearch(searchValue, urlSearch, (value) => {
    // The FFL pages have their own live search - don't navigate away while typing there.
    if (isFFLPage) return;
    const query = value.trim();
    if (!query) {
      if (!isProductPage) return;
      const currentParams = new URLSearchParams(location.search);
      currentParams.delete(PRODUCT_FILTER_QUERY_KEYS.SEARCH);
      navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });
      return;
    }
    navigate(
      `${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.SEARCH}=${encodeURIComponent(query)}`,
      { replace: isProductPage }
    );
  });

  const handleFilterClick = () => {
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('mobileFilter', 'open');
    navigate(`${location.pathname}?${currentParams.toString()}`);
  };

  // const handleSortClick = () => {
  //   const currentParams = new URLSearchParams(window.location.search);
  //   currentParams.set('mobileSort', 'open');
  //   navigate(`${location.pathname}?${currentParams.toString()}`);
  // };

  return (
    <div className="mobile-header-section d-lg-none">
      {/* Top Header Row */}
      <div className="mobile-header-section__top">
        <button
          className="mobile-header-section__icon-btn"
          aria-label={t('menu')}
          onClick={handleShow}
        >
          <HamburgerFullSvg />
        </button>

        <a href="/" className="mobile-header-section__logo">
          <img
            className="logo-image"
            src={theme === 'light' ? AppLogoHeaderLight : AppLogoHeader}
            alt="AppLogoHeader"
          />
        </a>

        <div className="mobile-header-section__actions">
          <MinCartSection />
        </div>
      </div>

      {/* Search Bar */}
      {/* {!isHomePage && ( */}
      <div className="mobile-header-section__search-row">
        <div className="mobile-header-section__search-container">
          {isProductPage && (
            <button
              className="mobile-header-section__filter-btn"
              onClick={handleFilterClick}
              aria-label={t('filter')}
            >
              <FilterAdjustmentSvg />
            </button>
          )}
          <div className="mobile-header-section__search">
            <input
              type="text"
              placeholder={placeholderText}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
              aria-label={t('search')}
            />
            <button className="search-btn" aria-label={t('search')} onClick={handleSearchSubmit}>
              <MagnifierSvg />
            </button>
          </div>
        </div>
      </div>
      {/* )} */}

      {/* Category Menu */}
      {categoryData && categoryData.length > 0 && (
        <div
          className={`mobile-category-menu ${scrollEdges.left ? 'has-scroll-left' : ''} ${scrollEdges.right ? 'has-scroll-right' : ''}`}
        >
          <div className="mobile-category-menu__list" ref={scrollRef} onScroll={updateScrollEdges}>
            <div className="mobile-category-menu__item">
              <Link to={placeParamsToURL(customerMainRoutes.productList.path)}>
                {t('all', { defaultValue: 'All' })}
              </Link>
            </div>

            {categoryData.map((item: any) => {
              const hasChildren = item.children && item.children.length > 0;
              return (
                <div key={item.id} className="mobile-category-menu__item">
                  {hasChildren ? (
                    <button
                      type="button"
                      className={`mobile-category-menu__btn ${activeCategoryDropdown?.id === item.id ? 'is-active' : ''}`}
                      onClick={(e) => handleCategoryClick(e, item)}
                    >
                      {item.name}
                      <ChevronDownSvg />
                    </button>
                  ) : (
                    <Link
                      to={`${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.CATEGORY}=${item.slug}`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          {scrollEdges.left && (
            <button
              type="button"
              className="mobile-category-menu__scroll-btn mobile-category-menu__scroll-btn--left"
              onClick={() => handleScrollBy(-180)}
              aria-label={t('scroll_left', { defaultValue: 'Scroll Left' })}
            >
              <ChevronLeftSvg />
            </button>
          )}
          {scrollEdges.right && (
            <button
              type="button"
              className="mobile-category-menu__scroll-btn"
              onClick={() => handleScrollBy(180)}
              aria-label={t('scroll_right', { defaultValue: 'Scroll Right' })}
            >
              <ChevronRightSvg />
            </button>
          )}

          {activeCategoryDropdown && dropdownPosition && (
            <>
              <div
                className="mobile-category-menu__backdrop"
                onClick={() => {
                  setActiveCategoryDropdown(null);
                  setDropdownPosition(null);
                }}
              />
              <div
                className="mobile-category-menu__dropdown"
                style={{ left: dropdownPosition.left }}
              >
                <Link
                  to={`${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.CATEGORY}=${activeCategoryDropdown.slug}`}
                  className="mobile-category-menu__dropdown-link all-link"
                  onClick={() => {
                    setActiveCategoryDropdown(null);
                    setDropdownPosition(null);
                  }}
                >
                  {t('all')}
                </Link>
                {activeCategoryDropdown.children.map((child: any) => (
                  <Link
                    key={child.id}
                    to={`${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.SUB_CATEGORY}=${child.slug}`}
                    className="mobile-category-menu__dropdown-link"
                    onClick={() => {
                      setActiveCategoryDropdown(null);
                      setDropdownPosition(null);
                    }}
                  >
                    {child.name}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Delivery Location */}
      {/* {!isHomePage && ( */}
      <div className="mobile-header-section__delivery-row">
        <div className="mobile-header-section__delivery">
          <DeliveryLocationBlock />
        </div>
      </div>
      {/* )} */}

      {/* Offcanvas Menu */}
      <Offcanvas
        show={showOffcanvas}
        onHide={handleClose}
        placement="start"
        className="mobile-offcanvas-menu"
      >
        <Offcanvas.Header closeButton closeVariant="white">
          <div className="social-icons d-flex gap-2">
            <a href="#">
              <img src={CustomerFacebook} alt="CustomerFacebook" />
            </a>
            <a href="#">
              <img src={CustomerTwitter} alt="CustomerTwitter" />
            </a>
            <a href="#">
              <img src={CustomerInstagram} alt="CustomerInstagram" />
            </a>
            <a href="#">
              <img src={CustomerYoutube} alt="CustomerYoutube" />
            </a>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {!userProfileInfo && (
            <div className="offcanvas-signup-buttons">
              <button
                className="offcanvas-signup-btn ffl-signup-btn flex-grow-1"
                onClick={() => {
                  handleClose();
                  navigate(placeParamsToURL(loginRoutes.signup.path));
                }}
              >
                {t('ffl_signup_text')}
              </button>
              <button
                className="offcanvas-signup-btn customer-signup-btn flex-grow-1"
                onClick={() => {
                  handleClose();
                  navigate(placeParamsToURL(loginRoutes.customerSignup.path));
                }}
              >
                {t('customer_signup')}
              </button>
            </div>
          )}

          {selectedFFLName && (
            <div className="offcanvas-delivery">
              <span className="offcanvas-delivery-text">
                {t('your_selected_ffl_for_shipping')} -{' '}
                <span
                  className="ffl-name cursor-pointer"
                  title={selectedFFLName}
                  onClick={handleOpenDeliveryLocation}
                >
                  {getTruncatedString(selectedFFLName, SHIPPING_FFL_NAME_MAX_LENGTH)}
                </span>
              </span>
              {isSelectedFFLRegistered && (
                <VerifiedBadgeSvg width={14} height={14} className="verified-check-icon" />
              )}
            </div>
          )}

          <div className="offcanvas-divider"></div>

          <div className="offcanvas-categories">
            <h3 className="offcanvas-section-title">{t('menu_section_shopping')}</h3>
            <Link to={placeParamsToURL(customerMainRoutes.productList.path)} onClick={handleClose}>
              SHOP ALL
            </Link>
            {categoryData?.map((item: any) => {
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = !!expandedCategories[item.id];
              return (
                <div key={item.id} className="offcanvas-category-wrapper">
                  <div className="offcanvas-category-item">
                    <Link
                      to={`${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.CATEGORY}=${item.slug}`}
                      onClick={handleClose}
                    >
                      {item.name}
                    </Link>
                    {hasChildren && (
                      <button
                        type="button"
                        className="plus-icon-btn"
                        onClick={(e) => toggleCategoryExpand(item.id, e)}
                        aria-label="Toggle subcategories"
                      >
                        {isExpanded ? <MinusMenuSvg /> : <PlusMenuSvg />}
                      </button>
                    )}
                  </div>
                  {hasChildren && (
                    <div
                      className={`offcanvas-subcategory-list ${isExpanded ? 'is-open' : 'is-collapsed'}`}
                    >
                      <Link
                        to={`${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.CATEGORY}=${item.slug}`}
                        onClick={handleClose}
                        className="offcanvas-subcategory-item all-link"
                      >
                        {t('all')}
                      </Link>
                      {item.children.map((child: any) => (
                        <Link
                          key={child.id}
                          to={`${placeParamsToURL(customerMainRoutes.productList.path)}?${PRODUCT_FILTER_QUERY_KEYS.SUB_CATEGORY}=${child.slug}`}
                          onClick={handleClose}
                          className="offcanvas-subcategory-item"
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="offcanvas-divider"></div>

          <div className="offcanvas-bottom-links">
            <h3 className="offcanvas-section-title">{t('menu_section_services')}</h3>
            <a href="#" onClick={handleSellClick}>
              <RefreshSvg className="sell-card__icon" />
              {t('firearm_trade_in')}
            </a>
            <Link to={placeParamsToURL(customerMainRoutes.fflListing.path)} onClick={handleClose}>
              <MapIconSvg />
              {t('gun_shop_locator')}
            </Link>
            <Link
              to={placeParamsToURL(customerProfileRoutes.compareList.path)}
              onClick={handleClose}
            >
              <CompareSvg />
              {t('compare_products', 'COMPARE PRODUCTS')}
            </Link>
          </div>

          <div className="offcanvas-divider"></div>

          <div className="offcanvas-bottom-links">
            <h3 className="offcanvas-section-title">{t('menu_section_for_gun_shops')}</h3>
            <a href="#" onClick={handleClose}>
              <UsersIconSvg />
              {t('why_gun_shops_join')}
            </a>
            {/* same label + link as the web top bar (FFL Login / FFL Dashboard / Admin Dashboard) */}
            <Link
              to={dashboardLink.path}
              reloadDocument={dashboardLink.isDashboard}
              onClick={handleClose}
            >
              <FflLoginIcon />
              {dashboardLink.label}
            </Link>
          </div>

          <div className="offcanvas-divider"></div>

          <div className="offcanvas-bottom-links">
            <h3 className="offcanvas-section-title">{t('menu_section_help_info')}</h3>
            <a href="#about" onClick={handleClose}>
              <ContactIconSvg />
              {t('about')}
            </a>
            <Link to={placeParamsToURL(customerMainRoutes.blogList.path)} onClick={handleClose}>
              <BlogIconSvg />
              {t('blog')}
            </Link>
          </div>

          <div className="social-icons d-flex">
            <a href="#">
              <img src={CustomerFacebook} alt="CustomerFacebook" />
            </a>
            <a href="#">
              <img src={CustomerTwitter} alt="CustomerTwitter" />
            </a>
            <a href="#">
              <img src={CustomerInstagram} alt="CustomerInstagram" />
            </a>
            <a href="#">
              <img src={CustomerYoutube} alt="CustomerYoutube" />
            </a>
          </div>
        </Offcanvas.Body>

        <div
          className="offcanvas-footer d-flex justify-content-between align-items-center"
          style={{ padding: '16px 24px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          <span className="offcanvas-footer__theme-label">{t('theme')}</span>
          <button
            onClick={toggleTheme}
            className={`top-section__theme-toggle ${theme === 'dark' ? 'is-dark' : 'is-light'}`}
            title={theme === 'dark' ? t('switch_to_light_mode') : t('switch_to_dark_mode')}
            aria-label="Toggle Theme"
          >
            <div className="theme-toggle-track">
              <span className="theme-toggle-icon moon-icon">
                <MoonSvg />
              </span>
              <span className="theme-toggle-icon sun-icon">
                <SunSvg />
              </span>
              <div className="theme-toggle-thumb" />
            </div>
          </button>
        </div>
      </Offcanvas>

      {showSellModal && (
        <Suspense fallback={<Preloader loading={true} />}>
          <SellYourFirearmsModal
            showModal={showSellModal}
            onClose={() => setShowSellModal(false)}
          />
        </Suspense>
      )}
    </div>
  );
};

export default MobileHeaderSection;

const ChevronDownSvg = () => (
  <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1 1L5 5L9 1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronLeftSvg = () => (
  <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5 9L1 5L5 1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRightSvg = () => (
  <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1 9L5 5L1 1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MinusMenuSvg = ({
  height = 24,
  width = 24,
  viewBox = '0 0 24 24',
}: {
  height?: any;
  width?: any;
  viewBox?: any;
}) => (
  <svg width={width} height={height} viewBox={viewBox} fill="none">
    <path
      d="M6 11C5.44772 11 5 11.4477 5 12C5 12.5523 5.44772 13 6 13H18C18.5523 13 19 12.5523 19 12C19 11.4477 18.5523 11 18 11H6Z"
      fill="#F54900"
    />
  </svg>
);

const FflLoginIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);
